import { useEffect, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'

import { categories, type AdminCategory } from '../api/endpoints'
import { Button } from '../components/Button'
import { DataTable } from '../components/DataTable'
import { Dialog, DialogClose, DialogContent } from '../components/Dialog'
import { FormField } from '../components/FormField'
import { Input } from '../components/Input'
import { PageLoader } from '../components/Spinner'

export function CategoriesPage() {
  const [data, setData] = useState<AdminCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [sortOrder, setSortOrder] = useState('0')

  function reload() {
    setLoading(true)
    categories
      .list()
      .then((r) => setData(r.categories))
      .catch((err) => toast.error('Failed to load categories', { description: err.message }))
      .finally(() => setLoading(false))
  }

  useEffect(reload, [])

  function resetForm() {
    setName(''); setSlug(''); setSortOrder('0')
  }

  function openCreate() { resetForm(); setCreating(true) }

  function openEdit(cat: AdminCategory) {
    setName(cat.name); setSlug(cat.slug); setSortOrder(String(cat.sortOrder)); setEditing(cat.id)
  }

  async function handleSave() {
    setSaving(true)
    try {
      const payload = {
        name,
        slug: slug || name.toLowerCase().replace(/\s+/g, '-'),
        sortOrder: parseInt(sortOrder, 10) || 0,
      }
      if (editing) {
        await categories.update(editing, payload)
        toast.success('Category updated')
      } else {
        await categories.create(payload)
        toast.success('Category created')
      }
      setCreating(false); setEditing(null); resetForm(); reload()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this category?')) return
    try {
      await categories.delete(id)
      toast.success('Category deleted')
      reload()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    }
  }

  const columns: ColumnDef<AdminCategory>[] = [
    { header: 'Name', accessorKey: 'name', cell: ({ getValue }) => <span className="font-medium">{String(getValue())}</span> },
    { header: 'Slug', accessorKey: 'slug', cell: ({ getValue }) => <code className="text-xs">{String(getValue())}</code> },
    { header: 'Products', accessorKey: 'productCount', cell: ({ getValue }) => <span className="tabular-nums">{String(getValue())}</span> },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => openEdit(row.original)}>Edit</Button>
          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(row.original.id)}>Delete</Button>
        </div>
      ),
    },
  ]

  if (loading) return <PageLoader />

  return (
    <div className="space-y-6 max-w-7xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-ink">Product Categories</h1>
          <p className="text-sm text-slatey mt-1">Manage the categories shown on the storefront</p>
        </div>
        <Button onClick={openCreate}><Plus className="size-4" />Add Category</Button>
      </div>

      <DataTable
        data={data} columns={columns}
        emptyTitle="No categories yet"
        emptyDescription="Create your first product category."
        emptyAction={<Button onClick={openCreate}><Plus className="size-4" />Add Category</Button>}
      />

      {(creating || editing) && (
        <Dialog open onOpenChange={() => { setCreating(false); setEditing(null); }}>
          <DialogContent title={editing ? 'Edit Category' : 'New Category'}>
            <div className="space-y-4">
              <FormField label="Name" required>
                <Input value={name} onChange={(e) => { setName(e.target.value); if (!editing) setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-')); }} placeholder="Weight Gain" />
              </FormField>
              <FormField label="Slug">
                <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="weight-gain" />
              </FormField>
              <FormField label="Sort Order">
                <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
              </FormField>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <DialogClose asChild>
                <Button variant="outline" onClick={() => { setCreating(false); setEditing(null); }}>Cancel</Button>
              </DialogClose>
              <Button onClick={handleSave} disabled={saving || !name}>
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
